import React, { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { ArrowRight, Bus, Save, Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function BusesPage() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 60_000,
  });
  const seasonYear = settings?.season_year || new Date().getFullYear();

  const { data: buses = [], isLoading } = useQuery({
    queryKey: ["buses"],
    queryFn: async () => (await api.get("/buses")).data,
  });

  const [edits, setEdits] = useState({});
  const [savingNum, setSavingNum] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const rows = useMemo(() => {
    return buses.map((b) => ({
      ...b,
      driver: edits[b.number]?.driver ?? b.driver,
      counselor: edits[b.number]?.counselor ?? b.counselor,
    }));
  }, [buses, edits]);

  const onChange = (num, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [num]: { ...(prev[num] || {}), [field]: value },
    }));
  };

  const saveRow = async (num) => {
    const patch = edits[num];
    if (!patch) return;
    setSavingNum(num);
    try {
      await api.patch(`/buses/${num}`, patch);
      toast.success(`Bus #${num} saved`);
      setEdits((prev) => {
        const { [num]: _, ...rest } = prev;
        return rest;
      });
      qc.invalidateQueries({ queryKey: ["buses"] });
    } catch (e) {
      toast.error(`Could not save Bus #${num}`);
    } finally {
      setSavingNum(null);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/buses/upload`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const json = await res.json();
      setUploadResult(json);
      const okCount = json.updated?.length || 0;
      const skipCount = json.skipped?.length || 0;
      if (okCount > 0) toast.success(`Updated ${okCount} bus${okCount === 1 ? "" : "es"}`);
      if (skipCount > 0) toast.warning(`Skipped ${skipCount} row${skipCount === 1 ? "" : "s"}`);
      if (okCount === 0 && skipCount === 0) toast.info("File was empty");
      qc.invalidateQueries({ queryKey: ["buses"] });
      setEdits({});
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const header = "Bus Number,Driver,Bus Counselor\n";
    const body = buses
      .map((b) => `${b.number},${(b.driver || "").replace(/,/g, " ")},${(b.counselor || "").replace(/,/g, " ")}`)
      .join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bus-roster-summer-${seasonYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="buses-page">
      <div className="mb-8">
        <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
          Tip Letters
        </div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B2545] mt-1">
          Bus Letters (1–33)
        </h1>
        <p className="text-[#13315C]/75 mt-2 max-w-xl text-sm sm:text-base">
          Each bus has one tip letter. Fill in the driver + bus counselor
          (same team all summer), then open the printable letter.
        </p>
      </div>

      {/* Bulk upload panel */}
      <div className="paper-card p-4 sm:p-5 mb-6" data-testid="bus-upload-panel">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex-1">
            <div className="font-mono-camp text-[10px] tracking-widest text-[#13315C]/60 uppercase">
              Bulk upload
            </div>
            <div className="font-display text-lg font-bold text-[#0B2545] leading-tight">
              CSV or Excel · 3 columns
            </div>
            <div className="text-xs text-[#13315C]/70 mt-1">
              Column 1: <b>bus number</b> · Column 2: <b>driver</b> · Column 3: <b>bus counselor</b>.
              Header row optional. Blank cells leave that field unchanged.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="bus-template-download"
              onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#E9DDC8] text-[#0B2545] text-xs font-semibold hover:border-[#0B2545]"
            >
              <Download className="w-3.5 h-3.5" /> Download template
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="hidden"
              data-testid="bus-upload-input"
              onChange={(e) => handleUpload(e.target.files?.[0])}
            />
            <button
              data-testid="bus-upload-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0B2545] text-[#FDF6EC] text-xs font-semibold hover:bg-[#13315C] disabled:opacity-70"
            >
              {uploading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
              ) : (
                <><Upload className="w-3.5 h-3.5" /> Upload CSV / Excel</>
              )}
            </button>
          </div>
        </div>
        {uploadResult && (
          <div className="mt-3 pt-3 border-t border-[#F1E7D3] text-xs" data-testid="bus-upload-result">
            <div className="text-[#0B2545]">
              <b>{uploadResult.updated?.length || 0}</b> bus row{(uploadResult.updated?.length || 0) === 1 ? "" : "s"} updated
              {(uploadResult.updated?.length || 0) > 0 && (
                <span className="text-[#13315C]/70"> — #{uploadResult.updated.join(", #")}</span>
              )}
            </div>
            {(uploadResult.skipped?.length || 0) > 0 && (
              <details className="mt-1 text-[#B54223]">
                <summary className="cursor-pointer">
                  {uploadResult.skipped.length} row{uploadResult.skipped.length === 1 ? "" : "s"} skipped
                </summary>
                <ul className="mt-1 ml-4 list-disc space-y-0.5 text-[#13315C]/70">
                  {uploadResult.skipped.map((s, i) => (
                    <li key={i}>Row {s.row}: {s.reason}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {isLoading && <div className="p-6 text-[#13315C]/60">Loading buses…</div>}

      {/* Mobile-first: stack rows as cards; desktop: table-like grid */}
      <div className="space-y-3 sm:space-y-0 sm:paper-card sm:overflow-hidden">
        {/* Desktop header (hidden on mobile) */}
        <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-3 text-[11px] font-mono-camp uppercase tracking-widest text-[#13315C]/60 border-b border-[#E9DDC8] bg-[#FBF7EF]">
          <div className="col-span-1">Bus</div>
          <div className="col-span-4">Driver</div>
          <div className="col-span-4">Bus Counselor</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>

        {rows.map((b) => {
          const dirty = !!edits[b.number];
          return (
            <div
              key={b.number}
              data-testid={`bus-row-${b.number}`}
              className="paper-card sm:paper-none sm:rounded-none sm:border-0 sm:border-b sm:border-[#F1E7D3] sm:shadow-none p-4 sm:p-0 sm:hover:bg-[#FBFAF7]"
            >
              {/* Mobile card layout */}
              <div className="sm:hidden">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#0B2545] text-[#FDF6EC] text-sm font-bold">
                      {b.number}
                    </span>
                    <span className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
                      Bus #{b.number}
                    </span>
                  </div>
                  <Link
                    to={`/letter/bus/${b.number}`}
                    data-testid={`bus-${b.number}-letter`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-[#0B2545] text-[#0B2545]"
                  >
                    <Bus className="w-3 h-3" /> Letter <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="space-y-2">
                  <label className="block">
                    <span className="text-[10px] font-mono-camp uppercase tracking-widest text-[#13315C]/60">
                      Driver
                    </span>
                    <input
                      data-testid={`bus-${b.number}-driver`}
                      value={b.driver}
                      onChange={(e) => onChange(b.number, "driver", e.target.value)}
                      placeholder="Driver name"
                      className="mt-0.5 w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-mono-camp uppercase tracking-widest text-[#13315C]/60">
                      Bus Counselor
                    </span>
                    <input
                      data-testid={`bus-${b.number}-counselor`}
                      value={b.counselor}
                      onChange={(e) => onChange(b.number, "counselor", e.target.value)}
                      placeholder="Bus Counselor name"
                      className="mt-0.5 w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                    />
                  </label>
                  <button
                    data-testid={`bus-${b.number}-save`}
                    disabled={!dirty || savingNum === b.number}
                    onClick={() => saveRow(b.number)}
                    className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition-colors ${
                      dirty
                        ? "bg-[#0B2545] text-[#FDF6EC] border-[#0B2545]"
                        : "bg-white text-[#13315C]/40 border-[#E9DDC8]"
                    }`}
                  >
                    <Save className="w-3.5 h-3.5" />
                    {savingNum === b.number ? "Saving…" : dirty ? "Save changes" : "No changes"}
                  </button>
                </div>
              </div>

              {/* Desktop row layout */}
              <div className="hidden sm:grid grid-cols-12 gap-2 items-center px-5 py-3">
                <div className="col-span-1 flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#0B2545] text-[#FDF6EC] text-sm font-bold">
                    {b.number}
                  </span>
                </div>
                <div className="col-span-4">
                  <input
                    value={b.driver}
                    onChange={(e) => onChange(b.number, "driver", e.target.value)}
                    placeholder="Driver name"
                    className="w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                  />
                </div>
                <div className="col-span-4">
                  <input
                    value={b.counselor}
                    onChange={(e) => onChange(b.number, "counselor", e.target.value)}
                    placeholder="Bus Counselor name"
                    className="w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                  />
                </div>
                <div className="col-span-3 flex items-center justify-end gap-2">
                  <button
                    disabled={!dirty || savingNum === b.number}
                    onClick={() => saveRow(b.number)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      dirty
                        ? "bg-[#0B2545] text-[#FDF6EC] border-[#0B2545] hover:bg-[#13315C]"
                        : "bg-white text-[#13315C]/40 border-[#E9DDC8]"
                    }`}
                  >
                    <Save className="w-3.5 h-3.5" />
                    {savingNum === b.number ? "Saving…" : "Save"}
                  </button>
                  <Link
                    to={`/letter/bus/${b.number}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-[#FDF6EC] transition-colors"
                  >
                    <Bus className="w-3.5 h-3.5" /> Letter <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
